import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowRight, ArrowLeft } from 'lucide-react';
import axios from 'axios';
import { SEO, SchemaOrg } from '../components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ComparisonDetailPage = () => {
  const { slug } = useParams();
  const [comparison, setComparison] = useState(null);
  const [allComparisons, setAllComparisons] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [compRes, allCompRes] = await Promise.all([
          axios.get(`${API}/comparisons/${slug}`),
          axios.get(`${API}/comparisons`)
        ]);
        setComparison(compRes.data);
        setAllComparisons(allCompRes.data);
      } catch (error) {
        console.error('Error fetching comparison:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    window.scrollTo(0, 0);
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-stone-300 border-t-stone-900 rounded-full animate-spin" />
      </div>
    );
  }

  if (!comparison) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center">
        <h1 className="heading-2 text-stone-900 mb-4">Comparison Not Found</h1>
        <Link to="/compare" className="btn-primary">View All Comparisons</Link>
      </div>
    );
  }

  const currentIndex = allComparisons.findIndex(c => c.slug === slug);
  const otherComparisons = allComparisons.filter(c => c.slug !== slug);

  return (
    <div data-testid={`comparison-page-${slug}`}>
      <SEO
        title={comparison.title}
        description={comparison.excerpt}
        url={`/compare/${comparison.slug}`}
        type="article"
      />
      <SchemaOrg
        type="breadcrumb"
        data={{
          items: [
            { name: 'Home', url: '/' },
            { name: 'Compare', url: '/compare' },
            { name: comparison.title, url: `/compare/${comparison.slug}` },
          ],
        }}
      />

      {/* Breadcrumb */}
      <div className="bg-stone-50 border-b border-stone-200 py-4">
        <div className="container-luxury">
          <nav className="breadcrumb">
            <Link to="/" className="breadcrumb-link">Home</Link>
            <span className="breadcrumb-separator">/</span>
            <Link to="/compare" className="breadcrumb-link">Compare</Link>
            <span className="breadcrumb-separator">/</span>
            <span className="text-stone-900 truncate max-w-[200px]">{comparison.title}</span>
          </nav>
        </div>
      </div>

      {/* Hero */}
      <section className="section-padding bg-stone-50">
        <div className="container-luxury">
          <div className="max-w-3xl">
            <span className="badge-gold mb-4">Comparison</span>
            <h1 className="heading-1 text-stone-900 mb-6">{comparison.title}</h1>
            <p className="body-lg">{comparison.excerpt}</p>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="section-padding">
        <div className="container-luxury">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-8">
              <article className="prose-luxury max-w-none">
                <div dangerouslySetInnerHTML={{ 
                  __html: comparison.content
                    .replace(/## /g, '<h2>')
                    .replace(/### /g, '<h3>')
                    .replace(/#### /g, '<h4>')
                    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                    .replace(/\n\n/g, '</p><p>')
                    .replace(/- (.*)/g, '<li>$1</li>')
                    .replace(/(<li>.*<\/li>)+/g, '<ul>$&</ul>')
                    .replace(/\| (.*) \| (.*) \| (.*) \| (.*) \|/g, '<tr><td>$1</td><td>$2</td><td>$3</td><td>$4</td></tr>')
                }} />
              </article>
            </div>

            <aside className="lg:col-span-4">
              <div className="sticky top-28">
                <div className="bg-stone-50 p-6 mb-6">
                  <h3 className="font-heading text-lg text-stone-900 mb-4">Other Comparisons</h3>
                  <div className="space-y-4">
                    {otherComparisons.map((c) => (
                      <Link
                        key={c.id}
                        to={`/compare/${c.slug}`}
                        className="block group"
                      >
                        <p className="text-sm font-medium text-stone-900 group-hover:text-amber-800 transition-colors">
                          {c.title}
                        </p>
                      </Link>
                    ))}
                  </div>
                </div>

                <div className="border border-stone-200 p-6">
                  <h3 className="font-heading text-lg text-stone-900 mb-4">Quick Links</h3>
                  <div className="space-y-3">
                    <Link 
                      to="/partners"
                      className="flex items-center justify-between p-3 bg-stone-50 hover:bg-stone-100 transition-colors"
                    >
                      <span className="text-sm text-stone-700">View All Partners</span>
                      <ArrowRight className="w-4 h-4 text-stone-400" />
                    </Link>
                    <Link 
                      to="/guides/buy-preowned-jewelry-online"
                      className="flex items-center justify-between p-3 bg-stone-50 hover:bg-stone-100 transition-colors"
                    >
                      <span className="text-sm text-stone-700">Buying Guide</span>
                      <ArrowRight className="w-4 h-4 text-stone-400" />
                    </Link>
                  </div>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ComparisonDetailPage;
